import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  AvatarGroup,
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CasinoIcon from '@mui/icons-material/Casino';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupIcon from '@mui/icons-material/Group';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import { useTranslation } from 'react-i18next';
import { fetchMyProfile } from '../api/companions';
import { fetchDashboardSummary } from '../api/dashboard';
import {
  acceptNightInvitation,
  createNightShareInvitation,
  declineNightInvitation,
  deleteNight,
  downloadNightCalendarFile,
  fetchUpcomingNight,
  removeNightCalendar,
  syncNightCalendar,
  updateNightVotes,
} from '../api/nights';
import BrandLogo from '../components/branding/BrandLogo';
import TavernChatPanel from '../components/chat/TavernChatPanel';
import PresenceBadge from '../components/companions/PresenceBadge';
import { getInitials } from '../components/companions/presence';
import { compactGameMetaLine, gameTitle } from '../components/games/gameFormat';
import GameCover from '../components/tavern/GameCover';
import SectionHeader from '../components/tavern/SectionHeader';
import { useToast } from '../components/tavern/ToastProvider';
import { nightInvitationResponseMessage } from '../utils/apiErrorMessages';
import ReportIssueButton from '../components/support/ReportIssueButton';
import { supportContextFromApiError } from '../utils/supportReports';
import {
  dateInputValueFromDateTime,
  formatDisplayDate,
  formatDisplayTime,
  formatDisplayWeekday,
  timeInputValueFromDateTime,
} from '../i18n/formatters';

const DAY = 24 * 60 * 60 * 1000;

const nightShareUrl = (token, origin = window.location.origin) =>
  `${origin}/invite/gathering/${encodeURIComponent(String(token || ''))}`;

const nightShareText = ({ inviterName, t, title, url }) =>
  t('nights.share.text', {
    inviterName: inviterName || t('profile.defaults.displayName'),
    title,
    url,
  });

const textWrapSx = {
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const invitationStatusForPerson = (night, person) => {
  if (!night || !person) return 'accepted';
  if (Number(person.id) === Number(night.hostId)) return 'accepted';

  const invitations = night.invitations || {};
  const personId = Number(person.id);

  if ((invitations.accepted || []).some((item) => Number(item.id) === personId)) return 'accepted';
  if ((invitations.declined || []).some((item) => Number(item.id) === personId)) return 'declined';
  if ((invitations.pending || []).some((item) => Number(item.id) === personId)) return 'pending';

  return invitations.requiresResponse ? 'pending' : 'accepted';
};

const invitationStatusLabelKey = (status) => {
  if (status === 'declined') return 'nights.invitations.declinedInvitations';
  if (status === 'pending') return 'nights.invitations.waitingForResponse';
  return 'nights.invitations.confirmedParticipants';
};

const invitationAvatarSx = (status, outlineColor = 'rgba(255,248,232,0.88)') => ({
  border: '3px solid',
  borderColor:
    status === 'declined'
      ? 'error.main'
      : status === 'pending'
        ? 'text.disabled'
        : 'success.main',
  outline: '2px solid',
  outlineColor,
  opacity: status === 'declined' ? 0.58 : 1,
});

const getDayDistance = (scheduledAt) => {
  const targetDate = dateInputValueFromDateTime(scheduledAt);
  const target = new Date(`${targetDate}T12:00:00`);
  const today = new Date();

  if (Number.isNaN(target.getTime())) return 0;

  today.setHours(12, 0, 0, 0);

  return Math.max(0, Math.ceil((target.getTime() - today.getTime()) / DAY));
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const validGameOptions = (games = []) =>
  asArray(games).filter((game) => {
    const id = Number(game?.id);

    return Number.isInteger(id) && id > 0;
  });

const validPositiveId = (value) => {
  const id = Number(value);

  return Number.isInteger(id) && id > 0 ? id : null;
};

const normalizeVoters = (voters = []) =>
  asArray(voters)
    .map((voter) => {
      if (!voter) return null;
      if (typeof voter === 'object') return voter;

      return { displayName: String(voter) };
    })
    .filter(Boolean);

const gameOwnerNames = (game, assignments = [], t) => {
  if (!game || typeof game !== 'object') return t('common.unknown');

  const owners = [
    game.ownerName,
    ...(asArray(game.owners).map((owner) => owner?.displayName)),
    ...asArray(assignments)
      .filter((assignment) => Number(assignment?.game?.id) === Number(game.id))
      .map((assignment) => assignment?.bringer?.displayName),
  ]
    .filter(Boolean)
    .map(String);

  const uniqueOwners = Array.from(new Set(owners));

  return uniqueOwners.length > 0 ? uniqueOwners.join(', ') : t('common.unknown');
};

export const buildGatheringPollOptions = (night, t, language) => {
  const availableGames = validGameOptions(night?.availableGames);
  const storedGames = validGameOptions(night?.games);
  const candidateGames = availableGames.length ? availableGames : storedGames;
  const results = asArray(night?.voting?.results)
    .map((result) => {
      const gameId = validPositiveId(result?.gameId);
      if (!gameId) return null;

      return {
        gameId,
        votes: Number(result?.votes) || 0,
        voters: normalizeVoters(result?.voters),
        votedByViewer: Boolean(result?.votedByViewer),
      };
    })
    .filter(Boolean);
  const resultByGameId = new Map(results.map((result) => [result.gameId, result]));
  const selectedGameIds = new Set(
    asArray(night?.voting?.viewerGameIds).map(validPositiveId).filter(Boolean)
  );

  return candidateGames
    .map((game) => {
      const result = resultByGameId.get(Number(game.id)) || {};

      return {
        game,
        gameId: Number(game.id),
        ownerLabel: gameOwnerNames(game, night?.assignments, t),
        title: gameTitle(game, t, language),
        votedByViewer: selectedGameIds.has(Number(game.id)) || Boolean(result.votedByViewer),
        voters: asArray(result.voters),
        votes: Number(result.votes) || 0,
      };
    })
    .filter((option) => option.votes > 0)
    .sort(
      (left, right) =>
        right.votes - left.votes ||
        Number(right.votedByViewer) - Number(left.votedByViewer) ||
        left.title.localeCompare(right.title, language, { sensitivity: 'base' })
    );
};

const GatheringVotePanel = ({
  canManageGames,
  language,
  night,
  onSuggestGame,
  onVote,
  t,
  votingGameId,
}) => {
  const options = useMemo(
    () => buildGatheringPollOptions(night, t, language),
    [language, night, t]
  );
  const voting = night?.voting || {};
  const votingOpen = Boolean(voting.open && night?.permissions?.canVote !== false);
  const maxVotes = Math.max(1, ...options.map((option) => option.votes));
  const selectedCount = options.filter((option) => option.votedByViewer).length;
  const totalVotes = options.reduce((total, option) => total + option.votes, 0);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.25 }}
      >
        <Box sx={textWrapSx}>
          <Typography variant="subtitle2" color="secondary.dark">
            {t('dashboard.gathering.voteEyebrow')}
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: 25, md: 30 }, ...textWrapSx }}>
            {t('dashboard.gathering.voteTitle')}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1.25,
            py: 0.75,
            borderRadius: 999,
            bgcolor: 'rgba(236, 170, 65, 0.16)',
            color: 'secondary.dark',
            fontWeight: 900,
            whiteSpace: 'nowrap',
          }}
        >
          {t('dashboard.gathering.voteTotal', { count: totalVotes })}
        </Box>
      </Stack>

      {options.length === 0 ? (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'rgba(15, 76, 69, 0.2)',
            borderRadius: 2,
            p: 2,
            bgcolor: 'rgba(15, 76, 69, 0.035)',
          }}
        >
          <Typography color="text.secondary" sx={textWrapSx}>
            {t('dashboard.gathering.noVoteOptions')}
          </Typography>
          {canManageGames ? (
            <Button
              variant="contained"
              startIcon={<CasinoIcon />}
              onClick={onSuggestGame}
              sx={{ mt: 1.5 }}
            >
              {t('dashboard.gathering.suggestAnother')}
            </Button>
          ) : null}
        </Box>
      ) : (
        <Stack spacing={1.15}>
          {options.map((option, index) => {
            const percent = Math.round((option.votes / maxVotes) * 100);
            const selected = option.votedByViewer;
            const disabled = !votingOpen || Boolean(votingGameId);

            return (
              <ButtonBase
                key={option.gameId}
                disabled={disabled}
                onClick={() => onVote(option.gameId)}
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'rgba(15, 76, 69, 0.16)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: selected ? 'rgba(15, 76, 69, 0.075)' : 'rgba(255, 250, 240, 0.62)',
                  opacity: disabled && !votingGameId ? 0.78 : 1,
                  transition: 'border-color 160ms ease, transform 160ms ease, background 160ms ease',
                  '&:hover': votingOpen
                    ? {
                        borderColor: 'secondary.main',
                        transform: 'translateY(-1px)',
                      }
                    : undefined,
                }}
              >
                <Box sx={{ p: { xs: 1.25, sm: 1.5 }, position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: `${percent}%`,
                      bgcolor:
                        index === 0 && option.votes > 0
                          ? 'rgba(236, 170, 65, 0.2)'
                          : 'rgba(15, 76, 69, 0.08)',
                      transition: 'width 220ms ease',
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ position: 'relative', minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: selected ? 28 : 24,
                        height: selected ? 28 : 24,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'rgba(15, 76, 69, 0.38)',
                        bgcolor: selected ? 'primary.main' : 'background.paper',
                        color: 'primary.contrastText',
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                      }}
                    >
                      {selected ? <CheckCircleIcon sx={{ fontSize: 19 }} /> : null}
                    </Box>
                    <GameCover
                      game={option.game}
                      size="small"
                      showBggRating={false}
                      sx={{ width: 48, flex: '0 0 auto' }}
                    />
                    <Box sx={{ flex: '1 1 auto', ...textWrapSx }}>
                      <Typography variant="h4" sx={{ fontSize: 17, ...textWrapSx }}>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                        {t('dashboard.gathering.ownedBy', { owners: option.ownerLabel })}
                      </Typography>
                    </Box>
                    <Stack spacing={0.4} alignItems="flex-end" sx={{ flex: '0 0 auto' }}>
                      <Typography fontWeight={900} sx={{ whiteSpace: 'nowrap' }}>
                        {t('nights.votes.count', { count: option.votes })}
                      </Typography>
                      {option.voters.length > 0 ? (
                        <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 11 } }}>
                          {option.voters.map((voter) => (
                            <Avatar
                              key={voter.id || voter.displayName}
                              src={voter.avatarUrl || undefined}
                              alt={voter.displayName || ''}
                              title={voter.displayName || ''}
                            >
                              {getInitials(voter.displayName || '?')}
                            </Avatar>
                          ))}
                        </AvatarGroup>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>
      )}

      {options.length > 0 ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
            {selectedCount > 0
              ? t('dashboard.gathering.yourVotes', { count: selectedCount })
              : votingOpen
                ? t('dashboard.gathering.tapToVote')
                : t('dashboard.gathering.votingClosed')}
          </Typography>
          {canManageGames ? (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onSuggestGame}
              sx={{
                minWidth: { xs: 0, sm: 210 },
                justifyContent: 'center',
                borderStyle: 'dashed',
                borderWidth: 2,
                whiteSpace: 'normal',
              }}
            >
              {t('dashboard.gathering.suggestAnother')}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const toast = useToast();
  const [upcomingNight, setUpcomingNight] = useState(null);
  const [topGames, setTopGames] = useState([]);
  const [topCompanions, setTopCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nightErrorKey, setNightErrorKey] = useState('');
  const [nightErrorContext, setNightErrorContext] = useState(null);
  const [profile, setProfile] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [votingGameId, setVotingGameId] = useState(null);
  const [respondingInvitation, setRespondingInvitation] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarFileLoading, setCalendarFileLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [nightActionAnchorEl, setNightActionAnchorEl] = useState(null);
  const [chatContext, setChatContext] = useState(null);
  const language = i18n.resolvedLanguage || i18n.language;
  const attendees = upcomingNight
    ? [upcomingNight.host, ...(upcomingNight.companions || [])].filter(Boolean)
    : [];
  const tableAttendees = upcomingNight
    ? attendees.map((person) => ({
        ...person,
        invitationStatus: invitationStatusForPerson(upcomingNight, person),
      }))
    : [];
  const canEditNight =
    upcomingNight?.permissions?.canEdit ?? upcomingNight?.hostId === upcomingNight?.viewerId;
  const canManageNightGames =
    upcomingNight?.permissions?.canManageGames ?? upcomingNight?.hostId === upcomingNight?.viewerId;
  const canManageNightCompanions =
    upcomingNight?.permissions?.canManageCompanions ??
    upcomingNight?.hostId === upcomingNight?.viewerId;
  const awaitingInvitationResponse = upcomingNight?.invitations?.viewerStatus === 'pending';
  const upcomingCalendar = upcomingNight?.calendar || {};
  const upcomingCalendarEvent = upcomingCalendar.event || null;
  const canDownloadCalendarFile = Boolean(upcomingNight) && !awaitingInvitationResponse;
  const canShareNight = canEditNight && !awaitingInvitationResponse;
  const nightActionMenuOpen = Boolean(nightActionAnchorEl);
  const canUseGoogleCalendar =
    Boolean(upcomingNight) &&
    !awaitingInvitationResponse &&
    (upcomingCalendar.connected || upcomingCalendar.needsReconnect);
  const gatheringDate = upcomingNight
    ? formatDisplayDate(upcomingNight.scheduledAt, language)
    : '';
  const gatheringWeekday = upcomingNight
    ? formatDisplayWeekday(upcomingNight.scheduledAt, language)
    : '';
  const gatheringTime = upcomingNight
    ? formatDisplayTime(timeInputValueFromDateTime(upcomingNight.scheduledAt), language)
    : '';
  const daysUntil = upcomingNight ? getDayDistance(upcomingNight.scheduledAt) : 0;
  const hasGuests = (upcomingNight?.companions || []).length > 0;
  const relativeNightDate = upcomingNight
    ? daysUntil === 0
      ? t('dashboard.hero.today')
      : daysUntil === 1
        ? t('dashboard.hero.tomorrow')
        : t('dashboard.hero.daysUntil', { count: daysUntil })
    : '';
  const relativeNightDateLabel = relativeNightDate
    ? `${relativeNightDate.charAt(0).toUpperCase()}${relativeNightDate.slice(1)}`
    : '';
  const gatheringDateLabel =
    upcomingNight && daysUntil <= 1
      ? `${relativeNightDateLabel} (${gatheringWeekday}, ${gatheringDate})`
      : `${gatheringWeekday}, ${gatheringDate}`;
  const heroRelativeDate =
    daysUntil > 1
      ? t('dashboard.hero.futureDate', { count: daysUntil })
      : relativeNightDate;

  const loadDashboard = useCallback(async (mountedRef = { current: true }, options = {}) => {
    const { showLoading = true } = options;

    if (showLoading) {
      setLoading(true);
    }
    setNightErrorKey('');
    setNightErrorContext(null);

    const [nightResult, profileResult, summaryResult] = await Promise.allSettled([
      fetchUpcomingNight(),
      fetchMyProfile(),
      fetchDashboardSummary(),
    ]);

    if (!mountedRef.current) return;

    if (nightResult.status === 'fulfilled') {
      setUpcomingNight(nightResult.value.night);
    } else {
      setUpcomingNight(null);
      setNightErrorKey('dashboard.feedback.loadError');
      setNightErrorContext(
        supportContextFromApiError(nightResult.reason, { pageName: 'Dashboard' })
      );
    }

    setProfile(profileResult.status === 'fulfilled' ? profileResult.value : null);

    if (summaryResult.status === 'fulfilled') {
      setTopGames(summaryResult.value.favoriteGames || []);
      setTopCompanions(summaryResult.value.topCompanions || []);
    } else {
      setTopGames([]);
      setTopCompanions([]);
    }

    if (showLoading) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const mountedRef = { current: true };

    loadDashboard(mountedRef);

    return () => {
      mountedRef.current = false;
    };
  }, [loadDashboard, location.key]);

  useEffect(() => {
    const handlePageShow = () => loadDashboard(undefined, { showLoading: false });
    const handleFocus = () => loadDashboard(undefined, { showLoading: false });
    const handleServerEvent = (event) => {
      if (event.detail?.type?.startsWith('night.') || event.detail?.type === 'presence.updated') {
        loadDashboard(undefined, { showLoading: false });
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('thetavern:server-event', handleServerEvent);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('thetavern:server-event', handleServerEvent);
    };
  }, [loadDashboard]);

  const handleCancelNight = async (sendNotifications) => {
    if (!upcomingNight) return;

    setDeleting(true);
    setNightErrorKey('');

    try {
      await deleteNight(upcomingNight.id, {
        sendNotifications,
        message: cancelMessage.trim() || null,
      });

      await loadDashboard(undefined, { showLoading: false });
      setCancelDialogOpen(false);
      setCancelMessage('');
      toast.success(t('nights.feedback.deleteSuccess'));
    } catch {
      toast.error(t('nights.feedback.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleVoteForGame = async (gameId) => {
    if (!upcomingNight || votingGameId || upcomingNight.permissions?.canVote === false) return;

    const currentVotes = (upcomingNight.voting?.viewerGameIds || [])
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0);
    const alreadySelected = currentVotes.includes(Number(gameId));
    const nextVotes = alreadySelected
      ? currentVotes.filter((id) => id !== Number(gameId))
      : [...currentVotes, Number(gameId)];

    setVotingGameId(Number(gameId));

    try {
      await updateNightVotes(upcomingNight.id, nextVotes);
      await loadDashboard(undefined, { showLoading: false });
      toast.success(t('nights.votes.saved'));
    } catch {
      toast.error(t('nights.votes.saveError'));
    } finally {
      setVotingGameId(null);
    }
  };

  const handleInvitationResponse = async (action) => {
    if (!upcomingNight || respondingInvitation) return;

    setRespondingInvitation(true);
    try {
      if (action === 'accept') {
        await acceptNightInvitation(upcomingNight.id);
        toast.success(t('nights.feedback.invitationAccepted'));
      } else {
        await declineNightInvitation(upcomingNight.id);
        toast.success(t('nights.feedback.invitationDeclined'));
      }

      await loadDashboard(undefined, { showLoading: false });
    } catch (error) {
      toast.error(nightInvitationResponseMessage(error, t));
      if ([404, 409].includes(Number(error?.response?.status))) {
        await loadDashboard(undefined, { showLoading: false });
      }
    } finally {
      setRespondingInvitation(false);
    }
  };

  const handleCalendarSync = async () => {
    if (!upcomingNight?.id || calendarLoading) return;

    setCalendarLoading(true);
    try {
      setUpcomingNight(await syncNightCalendar(upcomingNight.id));
      toast.success(t('integrations.googleCalendar.syncSuccess'));
    } catch {
      toast.error(t('integrations.googleCalendar.syncError'));
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleCalendarRemove = async () => {
    if (!upcomingNight?.id || calendarLoading) return;

    setCalendarLoading(true);
    try {
      setUpcomingNight(await removeNightCalendar(upcomingNight.id));
      toast.success(t('integrations.googleCalendar.removeSuccess'));
    } catch {
      toast.error(t('integrations.googleCalendar.removeError'));
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleCalendarFileDownload = async () => {
    if (!upcomingNight?.id || calendarFileLoading) return;

    setCalendarFileLoading(true);
    try {
      await downloadNightCalendarFile(upcomingNight.id, upcomingNight);
      toast.success(t('integrations.calendarFile.downloadSuccess'));
    } catch {
      toast.error(t('integrations.calendarFile.downloadError'));
    } finally {
      setCalendarFileLoading(false);
    }
  };

  const handleShareNight = async () => {
    if (!upcomingNight?.id || shareLoading) return;

    setShareLoading(true);
    try {
      const invitation = await createNightShareInvitation(upcomingNight.id);
      const url = nightShareUrl(invitation.token);
      const title = t('nights.share.title', { title: upcomingNight.title });
      const text = nightShareText({
        inviterName: upcomingNight.host?.displayName,
        t,
        title: upcomingNight.title,
        url,
      });

      if (navigator.share) {
        try {
          await navigator.share({ title, text, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      await navigator.clipboard?.writeText(text);
      toast.success(t('nights.feedback.shareCopied'));
    } catch {
      toast.error(t('nights.feedback.shareError'));
    } finally {
      setShareLoading(false);
    }
  };

  const closeNightActionMenu = () => setNightActionAnchorEl(null);
  const openUpcomingNightChat = () => {
    if (!upcomingNight) return;

    setChatContext({
      type: 'night',
      nightId: upcomingNight.id,
      title: t('chat.context.nightTitle', { title: upcomingNight.title }),
      subtitle: t('chat.context.nightSubtitle'),
      participants: attendees,
    });
  };

  return (
    <Stack spacing={{ xs: 5, md: 6 }}>
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          p: { xs: 3.5, md: 5 },
          minHeight: { md: 300 },
          color: '#fff8e8',
          bgcolor: 'primary.dark',
          boxShadow: '0 22px 54px -30px rgba(85, 33, 19, 0.8)',
          background:
            'radial-gradient(circle at 78% 95%, rgba(215,160,75,0.38), transparent 42%), linear-gradient(135deg, #0F4C45 0%, #0B3A35 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.24,
            background:
              'radial-gradient(circle at 10% 10%, rgba(255,255,255,0.25), transparent 18rem)',
          }}
        />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          alignItems="center"
          sx={{ position: 'relative' }}
        >
          <Box sx={{ flex: 1, maxWidth: 620 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <AutoAwesomeIcon sx={{ color: 'secondary.light', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ color: 'secondary.light' }}>
                {profile?.displayName
                  ? t('dashboard.hero.eyebrow', { name: profile.displayName })
                  : t('layout.welcomeBack')}
              </Typography>
            </Stack>
            <Typography variant="h1" sx={{ mb: 2 }}>
              {loading ? (
                <Skeleton variant="text" width="82%" sx={{ bgcolor: 'rgba(255,248,232,0.18)' }} />
              ) : upcomingNight ? (
                t('dashboard.hero.title')
              ) : (
                t('dashboard.hero.emptyTitle')
              )}
            </Typography>
            <Typography
              sx={{
                maxWidth: 520,
                color: 'rgba(255,248,232,0.86)',
                fontSize: { xs: '1rem', md: '1.1rem' },
                mb: 4,
              }}
            >
              {loading ? (
                <Stack spacing={1}>
                  <Skeleton variant="text" width="92%" sx={{ bgcolor: 'rgba(255,248,232,0.18)' }} />
                  <Skeleton variant="text" width="72%" sx={{ bgcolor: 'rgba(255,248,232,0.18)' }} />
                </Stack>
              ) : upcomingNight ? (
                <>
                  {t('dashboard.hero.subtitlePrefix')}{' '}
                  <Box
                    component="span"
                    sx={{
                      color: 'secondary.light',
                      fontWeight: 800,
                    }}
                  >
                    {heroRelativeDate}
                  </Box>
                  . {t('dashboard.hero.subtitleSuffix')}
                </>
              ) : (
                t('dashboard.hero.emptySubtitle')
              )}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<CasinoIcon />}
                disabled={loading}
                onClick={() => navigate(upcomingNight ? '/nights' : '/nights/new')}
              >
                {upcomingNight
                  ? t('dashboard.hero.openNight')
                  : t('dashboard.hero.primaryCta')}
              </Button>
              {awaitingInvitationResponse ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={respondingInvitation}
                    onClick={() => handleInvitationResponse('accept')}
                    sx={{ bgcolor: '#fff8e8', color: 'primary.dark' }}
                  >
                    {respondingInvitation
                      ? t('nights.invitations.answering')
                      : t('nights.invitations.accept')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={respondingInvitation}
                    onClick={() => handleInvitationResponse('decline')}
                    sx={{
                      color: '#fff8e8',
                      borderColor: 'rgba(255,248,232,0.5)',
                    }}
                  >
                    {t('nights.invitations.decline')}
                  </Button>
                </>
              ) : null}
              <Button
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon />}
                disabled={loading}
                onClick={() => navigate('/gameshelf')}
                sx={{
                  color: '#fff8e8',
                  borderColor: 'rgba(255,248,232,0.35)',
                  bgcolor: 'rgba(255,255,255,0.08)',
                  '&:hover': {
                    borderColor: 'rgba(255,248,232,0.55)',
                    bgcolor: 'rgba(255,255,255,0.12)',
                  },
                }}
              >
                {t('dashboard.hero.secondaryCta')}
              </Button>
            </Stack>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: 310,
              p: 3,
              borderRadius: 3,
              bgcolor: 'rgba(255, 250, 239, 0.92)',
              boxShadow: '0 24px 44px -34px rgba(0, 0, 0, 0.7)',
            }}
          >
            <BrandLogo variant="stacked" />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(300px, 0.75fr)' },
          gap: { xs: 4, lg: 3 },
        }}
      >
        <Box component="section">
          <SectionHeader
            title={t('dashboard.gathering.title')}
            action={t('dashboard.gathering.allAction')}
            onAction={() => navigate('/nights')}
          />
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
              {loading ? (
                <Stack spacing={2}>
                  <Skeleton variant="text" width="46%" height={42} />
                  <Skeleton variant="rounded" height={32} width="70%" />
                  <Skeleton variant="rounded" height={118} />
                </Stack>
              ) : null}

              {nightErrorKey ? (
                <Alert
                  severity="error"
                  action={
                    <ReportIssueButton
                      context={nightErrorContext}
                      sx={{ whiteSpace: 'nowrap' }}
                    />
                  }
                >
                  {t(nightErrorKey)}
                </Alert>
              ) : null}

              {!loading && !nightErrorKey && !upcomingNight ? (
                <Stack spacing={2} alignItems="flex-start">
                  <Typography variant="h3">{t('dashboard.gathering.emptyTitle')}</Typography>
                  <Typography color="text.secondary">
                    {t('dashboard.gathering.emptySubtitle')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/nights/new')}
                  >
                    {t('nights.actions.newNight')}
                  </Button>
                </Stack>
              ) : null}

              {!loading && !nightErrorKey && upcomingNight ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.95fr) minmax(0, 1.15fr)' },
                    mx: { xs: 0, md: -3 },
                    my: { xs: 0, md: -3 },
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 3, md: 4 },
                      minHeight: 320,
                      color: 'text.primary',
                      bgcolor: 'rgba(255,250,240,0.5)',
                      borderRight: { md: '1px solid' },
                      borderBottom: { xs: '1px solid', md: 0 },
                      borderColor: 'divider',
                      boxShadow: { md: 'inset 6px 0 0 #0F4C45' },
                      position: 'relative',
                    }}
                  >
                    {canEditNight || canShareNight ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 12, md: 16 },
                          right: { xs: 12, md: 16 },
                          zIndex: 2,
                        }}
                      >
                        <Tooltip title={t('common.actions')}>
                          <IconButton
                            aria-label={t('common.actions')}
                            aria-controls={nightActionMenuOpen ? 'dashboard-night-actions' : undefined}
                            aria-haspopup="menu"
                            aria-expanded={nightActionMenuOpen ? 'true' : undefined}
                            onClick={(event) => setNightActionAnchorEl(event.currentTarget)}
                            sx={{
                              bgcolor: 'rgba(255,248,232,0.94)',
                              color: 'primary.main',
                              boxShadow: '0 10px 22px -18px rgba(0,0,0,0.7)',
                              '&:hover': { bgcolor: 'secondary.light' },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          id="dashboard-night-actions"
                          anchorEl={nightActionAnchorEl}
                          open={nightActionMenuOpen}
                          onClose={closeNightActionMenu}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          {canShareNight ? (
                            <MenuItem
                              disabled={shareLoading}
                              onClick={() => {
                                closeNightActionMenu();
                                handleShareNight();
                              }}
                            >
                              <ListItemIcon>
                                <ShareIcon fontSize="small" />
                              </ListItemIcon>
                              {t('nights.actions.shareNight')}
                            </MenuItem>
                          ) : null}
                          {canEditNight ? (
                            <MenuItem
                              onClick={() => {
                                closeNightActionMenu();
                                navigate(`/nights/${upcomingNight.id}/edit`);
                              }}
                            >
                              <ListItemIcon>
                                <EditIcon fontSize="small" />
                              </ListItemIcon>
                              {t('nights.actions.editNight')}
                            </MenuItem>
                          ) : null}
                          {canEditNight ? (
                            <MenuItem
                              onClick={() => {
                                closeNightActionMenu();
                                setCancelDialogOpen(true);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <ListItemIcon sx={{ color: 'inherit' }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </ListItemIcon>
                              {t('nights.actions.cancelNight')}
                            </MenuItem>
                          ) : null}
                        </Menu>
                      </Box>
                    ) : null}

                    <Typography
                      variant="subtitle2"
                      color="secondary.dark"
                      sx={{ mb: 1, pr: canEditNight || canShareNight ? 6 : 0 }}
                    >
                      {t('nights.nextGathering')}
                    </Typography>
                    <Typography variant="h1" sx={{ fontSize: { xs: 32, md: 38 }, mb: 3, pr: { xs: 7, md: 2 } }}>
                      {upcomingNight.title}
                    </Typography>

                    <Stack spacing={1.75} sx={{ mb: 4 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <CalendarMonthIcon color="secondary" />
                        <Typography>{gatheringDateLabel}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <AccessTimeIcon color="secondary" />
                        <Typography>{gatheringTime}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocationOnIcon color="secondary" />
                        <Typography>{upcomingNight.locationName}</Typography>
                      </Stack>
                    </Stack>

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('nights.aroundTable')}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ pl: 1, minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}
                    >
                      {tableAttendees.map((person) => (
                        <Tooltip
                          key={person.id || person.displayName || person.username}
                          title={t(invitationStatusLabelKey(person.invitationStatus))}
                        >
                          <Avatar
                            src={person.avatarUrl || undefined}
                            alt={person.displayName || person.username || t('common.unknown')}
                            sx={{
                              ml: -1,
                              flex: '0 0 auto',
                              ...invitationAvatarSx(person.invitationStatus),
                            }}
                          />
                        </Tooltip>
                      ))}
                      {canManageNightCompanions ? (
                        <Avatar
                          component="button"
                          onClick={() => navigate(`/nights/${upcomingNight.id}/edit?step=companions`)}
                          aria-label={t('nights.actions.addCompanion')}
                          sx={{
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            ml: -1,
                            flex: '0 0 40px',
                            p: 0,
                            bgcolor: 'secondary.light',
                            color: 'primary.dark',
                            border: '2px solid',
                            borderColor: 'background.paper',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            appearance: 'none',
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </Avatar>
                      ) : null}
                    </Stack>
                  </Box>

                  <Box sx={{ p: { xs: 3, md: 4 }, minWidth: 0 }}>
                    <Stack spacing={2.25}>
                      <GatheringVotePanel
                        canManageGames={canManageNightGames}
                        language={language}
                        night={upcomingNight}
                        onSuggestGame={() => navigate(`/nights/${upcomingNight.id}/edit?step=games`)}
                        onVote={handleVoteForGame}
                        t={t}
                        votingGameId={votingGameId}
                      />

                      {upcomingNight && !awaitingInvitationResponse ? (
                        <Button
                          variant="contained"
                          startIcon={<ChatBubbleOutlineIcon />}
                          onClick={openUpcomingNightChat}
                          sx={{ width: '100%', whiteSpace: 'normal' }}
                        >
                          {t('chat.actions.openNight')}
                        </Button>
                      ) : null}

                      {canDownloadCalendarFile ? (
                        <Button
                          variant="outlined"
                          startIcon={<CalendarMonthIcon />}
                          disabled={calendarFileLoading}
                          onClick={handleCalendarFileDownload}
                          sx={{
                            width: '100%',
                            whiteSpace: 'normal',
                          }}
                        >
                          {calendarFileLoading
                            ? t('integrations.calendarFile.downloading')
                            : t('integrations.calendarFile.addToCalendar')}
                        </Button>
                      ) : null}

                      {canUseGoogleCalendar ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <Button
                            variant={upcomingCalendarEvent?.syncStatus === 'synced' ? 'outlined' : 'contained'}
                            color={upcomingCalendar.needsReconnect ? 'warning' : 'primary'}
                            startIcon={<CalendarMonthIcon />}
                            disabled={calendarLoading || upcomingCalendar.needsReconnect}
                            onClick={handleCalendarSync}
                            sx={{
                              flex: 1,
                              whiteSpace: 'normal',
                            }}
                          >
                            {upcomingCalendar.needsReconnect
                              ? t('integrations.googleCalendar.reconnectRequired')
                              : upcomingCalendarEvent?.syncStatus === 'synced'
                                ? t('integrations.googleCalendar.syncedOnCard')
                                : t('integrations.googleCalendar.addToCalendar')}
                          </Button>
                          {upcomingCalendarEvent ? (
                            <Button
                              variant="text"
                              color="inherit"
                              disabled={calendarLoading}
                              onClick={handleCalendarRemove}
                            >
                              {t('integrations.googleCalendar.removeFromCalendar')}
                            </Button>
                          ) : null}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Box>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Box>

        <Box component="section">
          <SectionHeader
            title={t('dashboard.companions.title')}
            subtitle={t('dashboard.companions.subtitle')}
            action={t('dashboard.companions.action')}
            onAction={() => navigate('/friends')}
          />
          <Card variant="outlined">
            <CardContent>
              {topCompanions.length === 0 ? (
                <Stack spacing={2} alignItems="flex-start">
                  <Typography variant="h3">{t('dashboard.companions.emptyTitle')}</Typography>
                  <Typography color="text.secondary">
                    {t('dashboard.companions.emptySubtitle')}
                  </Typography>
                  <Button variant="contained" color="primary" startIcon={<GroupIcon />} onClick={() => navigate('/friends')}>
                    {t('dashboard.companions.inviteCta')}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  {topCompanions.map((companion, index) => (
                    <Stack
                      key={companion.id}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: index === 0 ? 'secondary.light' : 'divider',
                        borderRadius: 2,
                        bgcolor: index === 0 ? 'rgba(236, 170, 65, 0.08)' : 'rgba(255,250,240,0.35)',
                      }}
                    >
                      <Avatar src={companion.avatarUrl || undefined} alt={companion.displayName}>
                        {getInitials(companion.displayName)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h4" noWrap>{companion.displayName}</Typography>
                        <PresenceBadge presence={companion.presence} size="small" noWrap />
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'secondary.dark',
                          textAlign: 'right',
                          minWidth: 54,
                        }}
                      >
                        {t('dashboard.companions.gamesKept', { count: companion.gamesKept || 0 })}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box component="section">
        <SectionHeader
          title={t('dashboard.treasury.title')}
          subtitle={t('dashboard.treasury.subtitle')}
          action={t('dashboard.treasury.action')}
          onAction={() => navigate('/gameshelf')}
        />
        {topGames.length === 0 ? (
          <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
            <CardContent>
              <Stack spacing={2} alignItems="flex-start">
                <Typography variant="h3">{t('dashboard.treasury.emptyTitle')}</Typography>
                <Typography color="text.secondary">{t('dashboard.treasury.emptySubtitle')}</Typography>
                <Button variant="contained" color="secondary" startIcon={<Inventory2OutlinedIcon />} onClick={() => navigate('/gameshelf')}>
                  {t('shelf.actions.addGame')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {topGames.map((game, index) => (
              <Card
                key={game.id}
                component="button"
                type="button"
                variant="outlined"
                onClick={() => navigate('/gameshelf')}
                sx={{
                  p: 0,
                  textAlign: 'left',
                  cursor: 'pointer',
                  bgcolor: index === 0 ? 'rgba(236, 170, 65, 0.08)' : 'rgba(255,250,240,0.36)',
                  '&:hover': { borderColor: 'secondary.main', transform: 'translateY(-2px)' },
                  transition: 'transform 160ms ease, border-color 160ms ease',
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 1.25, sm: 2 },
                    '&:last-child': { pb: { xs: 1.25, sm: 2 } },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 2 }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Box sx={{ position: 'relative', width: { xs: '100%', sm: 'auto' } }}>
                      <GameCover
                        game={game}
                        size="small"
                        sx={{ width: { xs: '100%', sm: 64 } }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -8,
                          left: -8,
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: index === 0 ? 'secondary.main' : 'primary.main',
                          color: index === 0 ? 'secondary.contrastText' : 'primary.contrastText',
                          fontWeight: 900,
                          fontSize: 12,
                          boxShadow: 2,
                        }}
                      >
                        {index + 1}
                      </Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {gameTitle(game, t, language)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {compactGameMetaLine(game, t)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => (deleting ? null : setCancelDialogOpen(false))}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, bgcolor: 'background.default' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {hasGuests ? t('nights.cancelDialog.titleWithGuests') : t('nights.cancelDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: hasGuests ? 2 : 0 }}>
            {hasGuests ? t('nights.cancelDialog.subtitleWithGuests') : t('nights.cancelDialog.subtitle')}
          </Typography>
          {hasGuests ? (
            <TextField
              value={cancelMessage}
              onChange={(event) => setCancelMessage(event.target.value)}
              placeholder={t('nights.cancelDialog.messagePlaceholder')}
              multiline
              minRows={4}
              fullWidth
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button disabled={deleting} onClick={() => setCancelDialogOpen(false)}>
            {t('nights.cancelDialog.backToEditing')}
          </Button>
          {hasGuests ? (
            <>
              <Button disabled={deleting} onClick={() => handleCancelNight(false)}>
                {t('nights.cancelDialog.doNotSend')}
              </Button>
              <Button variant="contained" disabled={deleting} onClick={() => handleCancelNight(true)}>
                {deleting ? t('nights.cancelDialog.cancelling') : t('nights.cancelDialog.send')}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="error"
              disabled={deleting}
              onClick={() => handleCancelNight(false)}
            >
              {deleting ? t('nights.cancelDialog.cancelling') : t('nights.cancelDialog.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <TavernChatPanel
        context={chatContext || {}}
        open={Boolean(chatContext)}
        onClose={() => setChatContext(null)}
      />
    </Stack>
  );
};

export default DashboardPage;
